import { useEffect, useCallback } from 'react'
import type Hls from 'hls.js'
import { useVideoPlaybackStore, type VideoContext } from '../stores/videoPlaybackStore'

export function useVideoPlayback(videoId: string, context: VideoContext) {
  const activeVideoId = useVideoPlaybackStore((s) => s.activeVideoId)
  const globalMuted = useVideoPlaybackStore((s) => s.globalMuted)
  const isManuallyPaused = useVideoPlaybackStore((s) => s.videos[videoId]?.isManuallyPaused ?? false)
  // Boolean, so this re-renders only when the ratio crosses zero, not on every
  // observer tick (`visibility` is the IntersectionObserver ratio written by
  // `useVideoVisibility`, which every surface mounts and which observes every
  // registered container regardless of context).
  const isOnScreen = useVideoPlaybackStore((s) => (s.videos[videoId]?.visibility ?? 0) > 0)

  const isPlaying = activeVideoId === videoId && !isManuallyPaused
  // Whether this player still owns playback *and is on screen*. A manual pause
  // clears `activeVideoId` (see `togglePlay`), so a paused player counts as
  // active until another video takes over (autoplay or a press) or its card
  // leaves the viewport — a paused player scrolled away must not keep its
  // speed menu open with focus off screen.
  const ownsPlayback = activeVideoId === videoId || (isManuallyPaused && activeVideoId === null)
  const isActive = ownsPlayback && isOnScreen
  const isMuted = globalMuted

  useEffect(() => {
    useVideoPlaybackStore.getState().registerVideo(videoId, context)
    return () => {
      useVideoPlaybackStore.getState().unregisterVideo(videoId)
    }
  }, [videoId, context])

  // Sync play/pause to video element
  useEffect(() => {
    const video = useVideoPlaybackStore.getState().videos[videoId]?.videoElement
    if (!video) return
    if (isPlaying) {
      video.play().catch(() => {})
    } else {
      video.pause()
    }
  }, [isPlaying, videoId])

  // Sync muted state
  useEffect(() => {
    const video = useVideoPlaybackStore.getState().videos[videoId]?.videoElement
    if (!video) return
    video.muted = isMuted
  }, [isMuted, videoId])

  // HLS bandwidth management: a player that loses playback stops loading,
  // including one released by a direct `activeVideoId` write (Studio's
  // preview dialog). Loading starts in the store (`setActiveVideo`,
  // `resumeActivePlayback`), never here: hls.js `startLoad` restarts loading,
  // so a second call would abort the fragment the store just requested.
  useEffect(() => {
    if (activeVideoId === videoId) return
    useVideoPlaybackStore.getState().videos[videoId]?.hlsInstance?.stopLoad()
  }, [activeVideoId, videoId])

  const togglePlay = useCallback(() => {
    const store = useVideoPlaybackStore.getState()
    const info = store.videos[videoId]
    if (!info) return
    if (isPlaying) {
      store.setManuallyPaused(videoId, true)
      store.setActiveVideo(null)
    } else {
      store.setManuallyPaused(videoId, false)
      store.setActiveVideo(videoId)
    }
  }, [videoId, isPlaying])

  /**
   * Toggles the shared mute state. Unmuting is a press on this player: an
   * autoplaying one becomes the viewer's (autoplay no longer hands it over to
   * a more visible video), and one that is not playing starts, so the viewer
   * hears the video they pressed rather than another one. A player they
   * paused stays paused. Returns whether playback started.
   */
  const toggleMute = useCallback((): boolean => {
    const store = useVideoPlaybackStore.getState()
    const unmuting = store.globalMuted
    store.toggleMute()
    const info = store.videos[videoId]
    if (!unmuting || !info || info.isManuallyPaused) return false
    if (store.activeVideoId === videoId) {
      useVideoPlaybackStore.setState({ activeStartedBy: 'viewer' })
      return false
    }
    store.setActiveVideo(videoId)
    return true
  }, [videoId])

  const setVideoElement = useCallback((el: HTMLVideoElement | null) => {
    useVideoPlaybackStore.getState().setVideoElement(videoId, el)
  }, [videoId])

  const setContainerElement = useCallback((el: HTMLElement | null) => {
    useVideoPlaybackStore.getState().setContainerElement(videoId, el)
  }, [videoId])

  const setHlsInstance = useCallback((hls: Hls | null) => {
    const store = useVideoPlaybackStore.getState()
    if (store.videos[videoId]) {
      store.setHlsInstance(videoId, hls)
    }
  }, [videoId])

  return {
    /** This player owns playback (playing, or manually paused with no other video active) and is on screen. */
    isActive,
    isPlaying,
    isMuted,
    togglePlay,
    toggleMute,
    setVideoElement,
    setContainerElement,
    setHlsInstance,
  }
}
